<template>
  <div class="relative w-full s-select" :class="isDark && 'text-text-primary'">
    <label
      class="absolute top-0 left-0 text-[0.6rem] p-[4px]"
      :for="selectId"
      :class="{
        'text-text-primary': !errors[0] && !isDark,
        'text-red-600': errors[0],
        'text-text-muted': isReadOnly || isDark,
      }"
    >
      <span>{{ label }}</span>
    </label>
    <select
      :id="selectId"
      ref="select"
      :class="{
        'bg-surface-elevated text-text-primary': isDark,
        'bg-surface': !isDark,
        'border-border-default': !errors[0],
        'border-red-600': errors[0],
        'text-text-muted border-border-default cursor-not-allowed': isReadOnly,
      }"
      class="p-4 w-full rounded-xl border appearance-none dark:bg-surface-elevated dark:text-text-primary dark:border-border-default"
      :value="modelValue ?? ''"
      @change="change"
    >
      <option v-if="placeholder" disabled="disabled" :value="undefined">
        {{ placeholder }}
      </option>
      <option
        v-for="option in options"
        :key="option.value ?? option"
        :value="option.value ?? option"
      >
        {{ option.label || option.value || option }}
        <span v-if="option.notice" class="text-red-500">
          {{ option.notice }}
        </span>
      </option>
    </select>
  </div>
</template>

<script setup>
const props = defineProps({
  isDark: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  isReadOnly: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: null,
    default: undefined
  },
  errors: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const selectId = useId()
const select = ref(null)

const change = (event) => {
  const selectedValue = event.target.value
  const numericValue = selectedValue === '' ? selectedValue : (!isNaN(selectedValue) && !isNaN(parseFloat(selectedValue)) ? parseFloat(selectedValue) : selectedValue)
  emit('update:modelValue', numericValue)
  emit('change', numericValue)
}

// Sync select element value when modelValue changes (fixes hydration mismatch)
watch(() => props.modelValue, (newValue) => {
  if (import.meta.client && select.value && newValue !== undefined && newValue !== null) {
    const expectedValue = String(newValue)
    if (select.value.value !== expectedValue) {
      select.value.value = expectedValue
    }
  }
}, { immediate: true })

// Fix hydration mismatch on mount
onMounted(async () => {
  await nextTick()
  if (import.meta.client && select.value && props.modelValue !== undefined && props.modelValue !== null) {
    const expectedValue = String(props.modelValue)
    if (select.value.value !== expectedValue) {
      select.value.value = expectedValue
    }
  }
})
</script>

<style>
/* Dark mode: force native select option bg */
.dark .s-select select option {
  background-color: #1A2332;
  color: #F9FAFB;
}

.s-select:before,
.s-select:after {
  content: "";
  position: absolute;
  z-index: 25;
  right: 13px;
  border-color: currentColor transparent;
  border-style: solid;
  border-width: 5px;
  pointer-events: none;
}

.s-select:before {
  top: calc(50% - 6px);
  border-top-width: 0;
  border-bottom-width: 5px;
}

.s-select:after {
  top: calc(50% + 2px);
  border-top-width: 5px;
  border-bottom-width: 0;
}
</style>
